<script>
  import AdminNav from '@/components/admin/AdminNav.svelte';
  let { stats = {}, recent_reports = [], pending_moderation = {}, weekly_trends = [], recent_audit_logs = [] } = $props()

  function getMaxTrend() {
    if (!weekly_trends.length) return 1
    return Math.max(...weekly_trends.map(d => Math.max(d.posts, d.comments, d.users, d.reports)), 1)
  }

  function getOrgTypeLabel(type) {
    const labels = { public: '공기업', private: '민간', financial: '금융', government: '정부' }
    return labels[type] || type
  }
</script>

<div class="space-y-6">
  <AdminNav activeTab="dashboard" />

  <div>
    <!-- Stats Grid -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <div class="card card-bordered p-6 shadow-md">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-primary-50 dark:bg-primary/20 rounded-xl flex items-center justify-center">
            <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
          </div>
          <div>
            <div class="text-h1 text-content">{stats.users_count || 0}</div>
            <div class="text-caption text-content-tertiary">총 회원</div>
          </div>
        </div>
        <div class="mt-2 text-caption text-success">+{stats.users_today || 0} 오늘</div>
      </div>
      
      <div class="card card-bordered p-6 shadow-md">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-primary-50 dark:bg-primary/20 rounded-xl flex items-center justify-center">
            <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
          </div>
          <div>
            <div class="text-h1 text-content">{stats.posts_count || 0}</div>
            <div class="text-caption text-content-tertiary">총 게시글</div>
          </div>
        </div>
        <div class="mt-2 text-caption text-success">+{stats.posts_today || 0} 오늘</div>
      </div>
      
      <div class="card card-bordered p-6 shadow-md">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-primary-50 dark:bg-primary/20 rounded-xl flex items-center justify-center">
            <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-2H5a2 2 0 01-2-2V8a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2l-4 2z"/></svg>
          </div>
          <div>
            <div class="text-h1 text-content">{stats.comments_count || 0}</div>
            <div class="text-caption text-content-tertiary">총 댓글</div>
          </div>
        </div>
      </div>
      
      <div class="card card-bordered p-6 shadow-md">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-error-light dark:bg-error/20 rounded-xl flex items-center justify-center">
            <svg class="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>
          </div>
          <div>
            <div class="text-h1 text-content">{stats.pending_reports || 0}</div>
            <div class="text-caption text-content-tertiary">대기중 신고</div>
          </div>
        </div>
        {#if stats.flagged_content > 0}
          <div class="mt-2 text-caption text-warning">{stats.flagged_content} 플래그됨</div>
        {/if}
      </div>
    </div>
    
    <!-- Extended Stats Row -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <div class="card card-bordered p-4 shadow-sm">
        <div class="text-body2 text-content-tertiary">대기중 회원</div>
        <div class="text-h2 text-warning-dark dark:text-warning">{stats.users_pending || 0}</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm">
        <div class="text-body2 text-content-tertiary">정지된 회원</div>
        <div class="text-h2 text-error">{stats.users_banned || 0}</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm">
        <div class="text-body2 text-content-tertiary">블라인드 게시글</div>
        <div class="text-h2 text-orange-600 dark:text-orange-400">{stats.blinded_posts || 0}</div>
      </div>
      <div class="card card-bordered p-4 shadow-sm">
        <div class="text-body2 text-content-tertiary">등록 기관</div>
        <div class="text-h2 text-primary">{stats.organizations_count || 0}</div>
      </div>
    </div>

    <!-- Content & Trash Stats -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <a href="/admin/boards" class="card card-bordered p-4 shadow-sm hover:shadow-md transition-shadow">
        <div class="text-body2 text-content-tertiary">게시판</div>
        <div class="text-h2 text-primary">{stats.boards_count || 0}</div>
      </a>
      <a href="/admin/categories" class="card card-bordered p-4 shadow-sm hover:shadow-md transition-shadow">
        <div class="text-body2 text-content-tertiary">카테고리</div>
        <div class="text-h2 text-primary">{stats.categories_count || 0}</div>
      </a>
      <div class="card card-bordered p-4 shadow-sm border-error/30">
        <div class="text-body2 text-content-tertiary">휴지통</div>
        <div class="text-h2 text-error">{(stats.discarded_posts || 0) + (stats.discarded_comments || 0) + (stats.discarded_users || 0)}</div>
        <div class="text-caption text-gray-400 dark:text-dark-text-disabled mt-1">
          게시글 {stats.discarded_posts || 0} / 댓글 {stats.discarded_comments || 0} / 회원 {stats.discarded_users || 0}
        </div>
      </div>
      {#if stats.expiring_soon > 0}
        <div class="card card-bordered p-4 shadow-sm border-warning/30 bg-warning-light/30 dark:bg-warning/5">
          <div class="text-body2 text-warning-dark dark:text-warning">곧 영구삭제</div>
          <div class="text-h2 text-warning-dark dark:text-warning">{stats.expiring_soon}</div>
          <div class="text-caption text-warning-dark/70 dark:text-warning/70 mt-1">7일 이내 만료</div>
        </div>
      {:else}
        <div class="card card-bordered p-4 shadow-sm">
          <div class="text-body2 text-content-tertiary">곧 영구삭제</div>
          <div class="text-h2 text-gray-300 dark:text-dark-text-disabled">0</div>
        </div>
      {/if}
    </div>
    
    <!-- Organization Type Breakdown -->
    {#if stats.org_stats}
      <div class="card card-bordered p-6 mb-8 shadow-md">
        <h2 class="text-title text-content mb-4 flex items-center gap-2">
          <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"/></svg>
          기관 유형별 현황
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          {#each Object.entries(stats.org_stats) as [type, count]}
            <div class="flex items-center justify-between p-3 bg-surface-tertiary rounded-xl">
              <span class="text-body2 text-content-secondary">{getOrgTypeLabel(type)}</span>
              <span class="text-body1 font-semibold text-content">{count}</span>
            </div>
          {/each}
        </div>
      </div>
    {/if}
    
    <!-- Weekly Trends Chart -->
    {#if weekly_trends.length > 0}
      <div class="card card-bordered p-6 mb-8 shadow-md">
        <h2 class="text-title text-content mb-4">최근 7일 추이</h2>
        <div class="flex items-end gap-1 h-40">
          {#each weekly_trends as day}
            {@const max = getMaxTrend()}
            <div class="flex-1 flex flex-col items-center gap-1">
              <div class="w-full flex items-end gap-0.5 h-28">
                <div class="flex-1 bg-blue-400 dark:bg-blue-500 rounded-t" style="height: {Math.max((day.posts / max) * 100, 2)}%" title="게시글 {day.posts}"></div>
                <div class="flex-1 bg-green-400 dark:bg-green-500 rounded-t" style="height: {Math.max((day.comments / max) * 100, 2)}%" title="댓글 {day.comments}"></div>
                <div class="flex-1 bg-purple-400 dark:bg-purple-500 rounded-t" style="height: {Math.max((day.users / max) * 100, 2)}%" title="가입 {day.users}"></div>
                <div class="flex-1 bg-red-400 dark:bg-red-500 rounded-t" style="height: {Math.max((day.reports / max) * 100, 2)}%" title="신고 {day.reports}"></div>
              </div>
              <span class="text-caption text-content-tertiary">{day.date}</span>
            </div>
          {/each}
        </div>
        <div class="flex items-center justify-center gap-6 mt-4 text-caption">
          <span class="flex items-center gap-1"><span class="w-3 h-3 bg-blue-400 rounded"></span> 게시글</span>
          <span class="flex items-center gap-1"><span class="w-3 h-3 bg-green-400 rounded"></span> 댓글</span>
          <span class="flex items-center gap-1"><span class="w-3 h-3 bg-purple-400 rounded"></span> 가입</span>
          <span class="flex items-center gap-1"><span class="w-3 h-3 bg-red-400 rounded"></span> 신고</span>
        </div>
      </div>
    {/if}

    <div class="grid md:grid-cols-2 gap-6">
      <!-- Pending Moderation -->
      <div class="card card-bordered overflow-hidden shadow-md">
        <div class="px-6 py-4 border-b border-default flex items-center justify-between">
          <h2 class="text-title text-content flex items-center gap-2">
            <svg class="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            모더레이션 대기
          </h2>
          <a href="/admin/posts?status=flagged" class="text-primary text-body2 hover:text-primary-dark transition-colors">전체보기</a>
        </div>
        
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#if pending_moderation.posts?.length > 0}
            {#each pending_moderation.posts as post}
              <a href={`/admin/posts/${post.id}`} class="block px-6 py-4 hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors">
                <div class="flex items-center justify-between">
                  <div>
                    <div class="text-body1 font-medium text-content truncate max-w-xs">{post.title}</div>
                    <div class="text-caption text-content-tertiary">by {post.user?.name || '익명'}</div>
                  </div>
                  <span class="badge {post.moderation_status === 'flagged' ? 'badge-warning' : 'badge-info'}">
                    {post.moderation_status}
                  </span>
                </div>
              </a>
            {/each}
          {:else}
            <div class="px-6 py-8 text-center text-content-tertiary">
              대기중인 콘텐츠가 없습니다
            </div>
          {/if}
        </div>
      </div>
      
      <!-- Recent Reports -->
      <div class="card card-bordered overflow-hidden shadow-md">
        <div class="px-6 py-4 border-b border-default flex items-center justify-between">
          <h2 class="text-title text-content flex items-center gap-2">
            <svg class="w-5 h-5 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>
            최근 신고
          </h2>
          <a href="/admin/reports" class="text-primary text-body2 hover:text-primary-dark transition-colors">전체보기</a>
        </div>
        
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#if recent_reports?.length > 0}
            {#each recent_reports as report}
              <a href={`/admin/reports/${report.id}`} class="block px-6 py-4 hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors">
                <div class="flex items-center justify-between">
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="text-body1 font-medium text-content">{report.reason}</span>
                      <span class="text-caption text-content-tertiary">{report.target_type}</span>
                    </div>
                    <div class="text-caption text-content-tertiary">신고자: {report.reporter?.name || '익명'}</div>
                  </div>
                  <span class="badge {report.status === 'pending' ? 'badge-warning' : 'badge-neutral'}">
                    {report.status}
                  </span>
                </div>
              </a>
            {/each}
          {:else}
            <div class="px-6 py-8 text-center text-content-tertiary">
              최근 신고가 없습니다
            </div>
          {/if}
        </div>
      </div>
    </div>
    
    <!-- Recent Audit Logs -->
    {#if recent_audit_logs.length > 0}
      <div class="mt-6 card card-bordered overflow-hidden shadow-md">
        <div class="px-6 py-4 border-b border-default flex items-center justify-between">
          <h2 class="text-title text-content">최근 관리자 활동</h2>
          <a href="/admin/audit_logs" class="text-primary text-body2 hover:text-primary-dark transition-colors">전체보기</a>
        </div>
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each recent_audit_logs as log}
            <div class="px-6 py-3 flex items-center justify-between">
              <div>
                <span class="text-body2 font-medium text-content">{log.action}</span>
                {#if log.target_label}
                  <span class="text-caption text-content-tertiary ml-1">— {log.target_label}</span>
                {/if}
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <span class="text-caption text-content-tertiary">{log.admin_name}</span>
                <span class="text-caption text-gray-400 dark:text-dark-text-disabled">
                  {new Date(log.created_at).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Quick Actions -->
    <div class="mt-8 card card-bordered p-6 shadow-md">
      <h2 class="text-title text-content mb-4 flex items-center gap-2">
        <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
        빠른 작업
      </h2>
      <div class="flex flex-wrap gap-3">
        <a href="/admin/users?status=pending" class="btn btn-secondary btn-sm">
          대기중 회원 확인
        </a>
        <a href="/admin/posts?status=flagged" class="btn btn-secondary btn-sm">
          플래그 게시글 검토
        </a>
        <a href="/admin/reports?status=pending" class="btn btn-secondary btn-sm">
          신고 처리하기
        </a>
        <a href="/admin/posts?status=deleted" class="btn btn-secondary btn-sm">
          삭제된 게시글
        </a>
        <a href="/admin/organizations" class="btn btn-secondary btn-sm">
          기관 관리
        </a>
        <a href="/admin/boards/new" class="btn btn-primary btn-sm">
          + 게시판 추가
        </a>
        <a href="/admin/categories/new" class="btn btn-primary btn-sm">
          + 카테고리 추가
        </a>
        <a href="/admin/organizations/new" class="btn btn-primary btn-sm">
          + 기관 추가
        </a>
      </div>
    </div>
  </div>
</div>
